import React from "react";
import VisorPluginPlaceholder from "../../_visor/components/canvas/VisorPluginPlaceholder";
import QuizStatusLine from "../../_visor/components/canvas/QuizStatusLine";
import { contentI18n as i18n } from "../../common/content_language";
import { setRgbaAlpha, shuffle, safeDecodeURI } from "../../common/common_tools";
import {
  DndContext,
  closestCenter,
  useDraggable,
  useDroppable,
  PointerSensor,
} from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import DragDropVisorPlaceholder from "./visor/DragDropVisorPlaceholder";
import { getDndCorrectAnswer } from "./dndSlots";
import AnswerButton from "../FreeResponse/visor/SubmitAnswerButton";
import DOMPurify from "dompurify";
import "./_dragdrop.scss";

/* eslint-disable react/prop-types */

// Display (pool) order for the draggable answers — shuffled when state.shuffle is
// on so the exercise isn't trivially "drag top-down in order". Scoring keys off
// each answer's own index, never its on-screen position.
function makeDisplayOrder(n, doShuffle) {
  let a = [];
  for (let i = 0; i < n; i++) a[i] = i;
  return doShuffle ? shuffle(a) : a;
}

// A draggable answer chip (lives in the pool). dnd-kit useDraggable — standard
// pointer events, so it actually drags (the old interactjs path was the broken
// "only the 1st answer drags / can't drag after check" bug).
function DraggableChip({ id, disabled, onPlace, children }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id,
    disabled,
  });
  // Translate the chip itself under the pointer (in place) instead of using a
  // DragOverlay. A DragOverlay uses position:fixed, which is positioned against
  // the wrong origin inside the scene's CSS transform → the chip "flew away".
  const style = {
    transform: CSS.Translate.toString(transform),
    ...(isDragging ? { zIndex: 100, position: "relative" } : {}),
  };
  return (
    <div
      ref={setNodeRef}
      style={{ ...style, cursor: disabled ? "default" : "pointer" }}
      className={"dnd-chip" + (isDragging ? " dnd-chip--dragging" : "")}
      {...(disabled ? {} : attributes)}
      {...(disabled ? {} : listeners)}
      // Tap/click (no drag) fills the next empty blank in order. The sensor's
      // distance constraint means a tap never starts a drag, so this fires
      // cleanly; onPlace itself guards against the click that trails a real drag.
      onClick={disabled ? undefined : onPlace}
    >
      {children}
    </div>
  );
}

// A drop slot rendered inline in the question text (replaces the empty
// `.dropzoneq` span the chip used to float over). When filled it shows the
// placed answer + an ✕ to clear it (per the agreed UX: no drag-back).
function DroppableSlot({ id, disabled, statusClass, hasHint, children }) {
  const { setNodeRef, isOver } = useDroppable({ id, disabled });
  return (
    <span
      ref={setNodeRef}
      className={
        "dropzoneq" +
        (isOver && !disabled ? " drop-active" : "") +
        (hasHint ? " has-hint" : "") +
        (statusClass ? " " + statusClass : "")
      }
    >
      {children}
    </span>
  );
}

export default class DragDropVisor extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      displayOrder: makeDisplayOrder(props.state.nBoxes, props.state.shuffle),
    };
    this.onDragEnd = this.onDragEnd.bind(this);
    // Distance constraint so a tap doesn't start a drag: a tap → click-to-fill
    // (next empty blank, in order); a real drag (>8px) → drop into a chosen slot.
    // Built here (not via the useSensors hook — this is a class component); the
    // shape matches useSensors' output ({ sensor, options }[]) and stays stable.
    this.sensors = [{ sensor: PointerSensor, options: { activationConstraint: { distance: 8 } } }];
    this._justDragged = false;
  }

  componentDidUpdate(prevProps) {
    // Regenerate the pool order when the count or shuffle flag changes.
    if (
      prevProps.state.nBoxes !== this.props.state.nBoxes ||
      prevProps.state.shuffle !== this.props.state.shuffle
    ) {
      this.setState({
        displayOrder: makeDisplayOrder(this.props.state.nBoxes, this.props.state.shuffle),
      });
    }
  }

  // The {{#N}} tokens in the question, in document order. Returns the answer
  // index expected in each slot (N-1) — the correct mapping, unchanged.
  getDropzoneIndexes() {
    return getDndCorrectAnswer(
      this.props.props.id,
      this.props.props.boxes,
      this.props.props.toolbars
    );
  }

  // Plain text of an answer container (used for the "Correct: …" hint).
  answerText(answerIdx) {
    try {
      const box = this.props.props.boxes[this.props.props.id];
      const c = box.sortableContainers["sc-Answer" + answerIdx];
      const cid = c && c.children && c.children[0];
      let t =
        cid && this.props.props.toolbars[cid] && this.props.props.toolbars[cid].state
          ? this.props.props.toolbars[cid].state.__text
          : "";
      t = safeDecodeURI(t || "")
        .replace(/^<p[^>]*>/i, "")
        .replace(/<\/p>$/i, "");
      return <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(t) }} />;
    } catch (e) {
      return null;
    }
  }

  // Place a pool chip into a slot. Only pool chips are draggable, so dropping
  // into an occupied slot simply overwrites it — the displaced chip returns to
  // the pool on re-render (it's no longer referenced by currentAnswer).
  onDragEnd(event) {
    // A real drag just ended → suppress the trailing native click so it doesn't
    // ALSO run click-to-fill and double-place. Cleared on the next tick, which
    // runs after the click has already fired.
    this._justDragged = true;
    setTimeout(() => {
      this._justDragged = false;
    }, 0);
    const { active, over } = event;
    if (!over) return;
    const chip = String(active.id).match(/^chip-(\d+)$/);
    const slot = String(over.id).match(/^slot-(\d+)$/);
    if (!chip || !slot) return;
    this.props.props.setAnswerForFib({ id: parseInt(slot[1], 10), ansval: parseInt(chip[1], 10) });
  }

  clearSlot(slotPos) {
    this.props.props.removeAnswerForDnd({ id: slotPos, ansval: -1 });
  }

  // Try Again: empty every slot (chips return to the pool) + reshuffle the pool.
  refreshAnswers() {
    const ca = this.props.props.exercises && this.props.props.exercises.currentAnswer;
    if (Array.isArray(ca)) {
      for (let i = 0; i < ca.length; i++) {
        if (ca[i] !== undefined && ca[i] !== null && ca[i] !== -1) {
          this.props.props.removeAnswerForDnd({ id: i, ansval: -1 });
        }
      }
    }
    this.setState({
      displayOrder: makeDisplayOrder(this.props.state.nBoxes, this.props.state.shuffle),
    });
  }

  render() {
    const attempted = this.props.props.exercises && this.props.props.exercises.attempted;
    const currentAnswer = this.props.props.exercises && this.props.props.exercises.currentAnswer;
    const isCA = Array.isArray(currentAnswer);

    const dropzoneIndexes = this.getDropzoneIndexes();
    const actualDropzoneCount = dropzoneIndexes.length || this.props.state.nBoxes;

    let score = this.props.props.exercises.score || 0;
    score = Math.round(score * 100) / 100;
    score = score + "/" + (this.props.props.exercises.weight || 0);

    // Placement maps.
    const placedBySlot = [];
    const placedSet = new Set();
    for (let s = 0; s < actualDropzoneCount; s++) {
      const v = isCA ? currentAnswer[s] : undefined;
      const ok = v !== undefined && v !== null && v !== -1;
      placedBySlot[s] = ok ? v : -1;
      if (ok) placedSet.add(v);
    }

    const allDropzonesFilled =
      isCA && actualDropzoneCount > 0 && placedBySlot.every((v) => v !== -1);
    const allDropzonesCorrect =
      allDropzonesFilled && placedBySlot.every((v, s) => v === dropzoneIndexes[s]);
    const istrue = attempted && allDropzonesFilled && allDropzonesCorrect;

    // Tekrar ("Try Again") modunda hiç kutu yanmaz (ne yeşil ne kırmızı) — cevap
    // açığa çıkmasın; sonuç yalnızca buton altındaki durum satırıyla bildirilir.
    // Diğer modlarda slot bazında yeşil/kırmızı, doğru ipucu seeAnswer'da.
    const isTryAgainMode =
      this.props.state.buttons === "tryAgain" ||
      this.props.state.buttons === i18n.t("MultipleChoice.tryAgain") ||
      this.props.state.buttons === "Try Again" ||
      this.props.state.buttons === "Tekrar Dene";
    const showAnswerStyles = attempted && (!isTryAgainMode || istrue);

    // `attempted` is per-exercise (submitPage only flags the submitted box), so
    // it alone gates feedback: submitting with every slot empty is still a
    // wrong answer and must say so.
    const showFeedback = attempted && this.props.state.showFeedback;

    // ---- Question text + inline drop slots (rendered here, not via VisorBox) ----
    let questionText = "";
    try {
      const qc = this.props.props.boxes[this.props.props.id].sortableContainers["sc-Question"];
      const qid = qc && qc.children && qc.children[0];
      questionText =
        qid && this.props.props.toolbars[qid] && this.props.props.toolbars[qid].state
          ? safeDecodeURI(this.props.props.toolbars[qid].state.__text || "")
          : "";
    } catch (e) {
      /* empty */
    }
    const qParts = questionText.split(/\{\{#(\d+)\}\}/);
    const questionEls = [];
    let slotPos = 0;
    for (let i = 0; i < qParts.length; i++) {
      if (i % 2 === 0) {
        if (qParts[i] && qParts[i].trim()) {
          const html = qParts[i].replace(/<p\s*[^>]*>/gi, "<span>").replace(/<\/p>/gi, "</span>");
          questionEls.push(
            <span key={"qt-" + i} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }} />
          );
        }
      } else {
        const sp = slotPos++;
        const placed = placedBySlot[sp];
        // Tekrar modunda renk yok (cevap gizli); diğer modlarda slot bazında yeşil/kırmızı.
        // Boş bırakılan slot da yanlıştır — aksi halde hiç cevap vermeden
        // "Cevabı kontrol et" hiçbir geri bildirim üretmiyordu.
        const isCorrect =
          attempted && !isTryAgainMode && placed !== -1 && placed === dropzoneIndexes[sp];
        const isIncorrect = attempted && !isTryAgainMode && !isCorrect;
        const statusClass = isCorrect
          ? "correct-dropzone"
          : isIncorrect
            ? "incorrect-dropzone"
            : "";
        const showHint = isIncorrect && showAnswerStyles;
        questionEls.push(
          <DroppableSlot
            key={"slot-" + sp}
            id={"slot-" + sp}
            disabled={!!attempted}
            statusClass={statusClass}
            hasHint={showHint}
          >
            {placed !== -1 ? (
              <span className="dnd-slot-filled">
                <DragDropVisorPlaceholder
                  {...this.props.props}
                  key={"placed-" + placed}
                  pluginContainer={"Answer" + placed}
                />
                {!attempted ? (
                  <button
                    type="button"
                    className="dnd-slot-clear"
                    aria-label={i18n.t("messages.remove_answer") || "Clear"}
                    title={i18n.t("messages.remove_answer") || "Clear"}
                    onClick={() => this.clearSlot(sp)}
                  >
                    ×
                  </button>
                ) : null}
              </span>
            ) : null}
            {showHint ? (
              <span className="correct-answer-hint">{this.answerText(dropzoneIndexes[sp])}</span>
            ) : null}
          </DroppableSlot>
        );
      }
    }

    // First empty slot in document order — where a click-to-fill chip lands, so
    // tapping answers fills the blanks one by one, left to right.
    const nextEmptySlot = () => {
      for (let s = 0; s < actualDropzoneCount; s++) {
        if (placedBySlot[s] === -1) return s;
      }
      return -1;
    };

    // ---- Answer pool (unplaced chips, in shuffled display order) ----
    const poolEls = this.state.displayOrder
      .filter((a) => a < this.props.state.nBoxes && !placedSet.has(a))
      .map((answerIdx) => (
        <DraggableChip
          key={"chip-" + answerIdx}
          id={"chip-" + answerIdx}
          disabled={!!attempted}
          onPlace={() => {
            if (attempted || this._justDragged) return;
            const s = nextEmptySlot();
            if (s !== -1) this.props.props.setAnswerForFib({ id: s, ansval: answerIdx });
          }}
        >
          <DragDropVisorPlaceholder
            {...this.props.props}
            key={"pool-" + answerIdx}
            pluginContainer={"Answer" + answerIdx}
          />
        </DraggableChip>
      ));

    // ---- Feedback (unchanged logic) ----
    const tfC = this.props.props.boxes[this.props.props.id].sortableContainers["sc-TrueFeedback"];
    const tfTb = tfC?.children?.[0] ? this.props.props.toolbars[tfC.children[0]] : null;
    const ffC = this.props.props.boxes[this.props.props.id].sortableContainers["sc-FalseFeedback"];
    const ffTb = ffC?.children?.[0] ? this.props.props.toolbars[ffC.children[0]] : null;

    const isDefaultFeedback = (tb, keys) => {
      const txt = (tb?.state?.__text || "").toString();
      return keys.some((k) => txt.includes(k));
    };
    const checkEmptyTrue =
      (!tfC?.children ||
        tfC.children.length === 0 ||
        (tfTb?.state?.__text || "").includes(i18n.t("text_here"))) &&
      !isDefaultFeedback(tfTb, [
        i18n.t("MultipleChoice.TrueFeedbackMsg"),
        i18n.t("DragDrop.TrueFeedbackMsg"),
      ]);
    const checkEmptyFalse =
      (!ffC?.children ||
        ffC.children.length === 0 ||
        (ffTb?.state?.__text || "").includes(i18n.t("text_here"))) &&
      !isDefaultFeedback(ffTb, [
        i18n.t("MultipleChoice.FalseFeedbackMsg"),
        i18n.t("DragDrop.FalseFeedbackMsg"),
      ]);

    return (
      <div
        className={
          "exercisePlugin dragdropPlugin" +
          (attempted ? " attempted " : " ") +
          (this.props.props.exercises.showFeedback ? "showFeedback" : "")
        }
      >
        <DndContext
          sensors={this.sensors}
          collisionDetection={closestCenter}
          onDragEnd={this.onDragEnd}
        >
          <div className={"row"} key={0}>
            <div className={"col-xs-12 dnd-question"}>{questionEls}</div>
          </div>
          <div className="dnd-pool">{poolEls}</div>
        </DndContext>

        {istrue && !checkEmptyTrue ? (
          <div
            className={"row feedbackRow"}
            key={-2}
            style={{ display: showFeedback ? "block" : "none" }}
          >
            <div
              className={"col-xs-12 feedback"}
              role="status"
              aria-live="polite"
              style={{
                color: "black",
                borderColor: "#318533",
                backgroundColor: setRgbaAlpha("rgba(39,208,124,0.20)", 0.15),
                display: "flex",
                alignItems: "center",
              }}
            >
              <VisorPluginPlaceholder
                {...this.props.props}
                key="0"
                pluginContainer={"TrueFeedback"}
              />
            </div>
          </div>
        ) : null}
        {!istrue && !checkEmptyFalse ? (
          <div
            className={"row feedbackRow"}
            key={-3}
            style={{ display: showFeedback ? "block" : "none" }}
          >
            <div
              className={"col-xs-12 feedback"}
              role="status"
              aria-live="polite"
              style={{
                color: "black",
                borderColor: "#a31111",
                backgroundColor: setRgbaAlpha("rgba(213,26,26,0.20)", 0.15),
                display: "flex",
                alignItems: "center",
              }}
            >
              <VisorPluginPlaceholder
                {...this.props.props}
                key="1232"
                pluginContainer={"FalseFeedback"}
              />
            </div>
          </div>
        ) : null}

        {isTryAgainMode && !this.props.state.showFeedback ? (
          <QuizStatusLine correct={istrue} visible={attempted} />
        ) : null}

        <div className={"exerciseScore"}>{score}</div>
        <div className={"row"} key={7}>
          <AnswerButton
            onClick={() => {
              this.refreshAnswers();
              this.props.props.tryAgainHandler();
            }}
            hidden={
              istrue ||
              !this.props.props.exercises.attempted ||
              (this.props.state.buttons != "tryAgain" &&
                this.props.state.buttons != i18n.t("MultipleChoice.tryAgain") &&
                this.props.state.buttons != "Try Again" &&
                this.props.state.buttons != "Tekrar Dene")
            }
            text={i18n.t("MultipleChoice.tryAgain")}
          />
          <AnswerButton
            onClick={() => this.props.props.seeAnswerHandler()}
            hidden={
              istrue ||
              !this.props.props.exercises.attempted ||
              (this.props.state.buttons != i18n.t("MultipleChoice.seeAnswer") &&
                this.props.state.buttons != "seeAnswer")
            }
            text={i18n.t("MultipleChoice.seeAnswer")}
          />
          <AnswerButton
            onClick={() =>
              this.props.props.submitPage(this.props.props.currentView, this.props.props.id)
            }
            hidden={this.props.props.exercises.attempted}
            text={i18n.t("MultipleChoice.checkAnswer")}
          />
        </div>
      </div>
    );
  }
}
